import { useState } from 'react';
import { apiList } from '../api/client';
import { audit, identity } from '../api/endpoints';
import type { AdminUser, AuditEvent, Role } from '../api/types';
import { Card, Column, DataTable, Empty, ErrorBox, Field, fmtDate, PageHeader } from '../components/ui';
import { useAsync } from '../hooks';

const ADMIN_LINKS = [
  { href: '/admin/', title: 'Django Admin', desc: 'Стандартная админ-панель backend' },
  { href: '/api/v1/schema/swagger/', title: 'Swagger UI', desc: 'Интерактивная документация API' },
  { href: '/api/v1/schema/redoc/', title: 'ReDoc', desc: 'OpenAPI-документация' },
  { href: '/api/v1/health/', title: 'Health check', desc: 'Состояние backend' },
];

const ADMIN_ENDPOINTS = [
  { path: '/workflow/workflow-rule/', title: 'Правила workflow' },
  { path: '/workflow/business-calendar/', title: 'Бизнес-календари' },
  { path: '/workflow/exception-approval/', title: 'Согласования исключений' },
  { path: '/identity/role/', title: 'Роли' },
  { path: '/finance/price-policy/', title: 'Ценовые политики' },
];

interface UserFormState {
  id: number | null;
  username: string;
  password: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  is_staff: boolean;
  is_superuser: boolean;
  role_codes: string[];
}

const EMPTY_USER_FORM: UserFormState = {
  id: null, username: '', password: '', first_name: '', last_name: '',
  email: '', is_active: true, is_staff: false, is_superuser: false,
  role_codes: [],
};

function UsersCard() {
  const users = useAsync<AdminUser[]>(() => identity.users(), []);
  const roles = useAsync<Role[]>(
    () => identity.roles().catch(() => [] as Role[]), []);
  const [form, setForm] = useState<UserFormState | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const openNew = () => { setErr(null); setForm({ ...EMPTY_USER_FORM }); };
  const openEdit = (u: AdminUser) => {
    setErr(null);
    setForm({
      id: u.id, username: u.username, password: '',
      first_name: u.first_name ?? '', last_name: u.last_name ?? '',
      email: u.email ?? '', is_active: u.is_active, is_staff: u.is_staff,
      is_superuser: u.is_superuser, role_codes: [...u.roles],
    });
  };

  const toggleRole = (code: string) => {
    if (!form) return;
    const next = new Set(form.role_codes);
    if (next.has(code)) next.delete(code); else next.add(code);
    setForm({ ...form, role_codes: [...next] });
  };

  const submit = async () => {
    if (!form) return;
    setBusy(true); setErr(null);
    try {
      const body = {
        username: form.username, first_name: form.first_name,
        last_name: form.last_name, email: form.email,
        is_active: form.is_active, is_staff: form.is_staff,
        is_superuser: form.is_superuser, role_codes: form.role_codes,
        ...(form.password ? { password: form.password } : {}),
      };
      if (form.id != null) {
        await identity.updateUser(form.id, body);
      } else {
        await identity.createUser({ ...body, password: form.password });
      }
      setForm(null);
      await users.reload();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`Удалить пользователя ${u.username}?`)) return;
    setErr(null);
    try {
      await identity.deleteUser(u.id);
      await users.reload();
    } catch (e) {
      setErr(e);
    }
  };

  const cols: Column<AdminUser>[] = [
    { key: 'username', title: 'Логин', render: (u) => <strong>{u.username}</strong> },
    {
      key: 'fio', title: 'Имя',
      render: (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || '—',
    },
    { key: 'email', title: 'Email', render: (u) => u.email || '—' },
    {
      key: 'roles', title: 'Роли',
      render: (u) => u.roles.join(', ') || '—',
    },
    {
      key: 'flags', title: 'Флаги', width: '170px',
      render: (u) => [
        u.is_active ? 'активен' : 'неактивен',
        u.is_superuser ? 'superuser' : '',
        u.is_staff ? 'staff' : '',
      ].filter(Boolean).join(', '),
    },
    {
      key: 'ops', title: '', width: '140px',
      render: (u) => (
        <span className="toolbar">
          <button className="btn small" onClick={() => openEdit(u)}>Изменить</button>
          <button className="btn small danger" onClick={() => void remove(u)}>Удалить</button>
        </span>
      ),
    },
  ];

  return (
    <Card
      title="Пользователи"
      actions={(
        <span className="toolbar">
          <button className="btn" onClick={users.reload}>Обновить</button>
          <button className="btn primary" onClick={openNew}>+ Пользователь</button>
        </span>
      )}
    >
      <ErrorBox error={err} />
      <ErrorBox error={users.error} onRetry={users.reload} />
      {form && (
        <div className="inline-form" data-testid="admin-user-form">
          <Field label="Логин">
            <input value={form.username}
                   onChange={(e) => setForm({ ...form, username: e.target.value })} />
          </Field>
          <Field label={form.id != null ? 'Пароль (пусто — оставить прежний)' : 'Пароль (мин. 8)'}>
            <input type="password" value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </Field>
          <Field label="Имя">
            <input value={form.first_name}
                   onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </Field>
          <Field label="Фамилия">
            <input value={form.last_name}
                   onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </Field>
          <Field label=" ">
            <label><input type="checkbox" checked={form.is_active}
                          onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> активен</label>
            <label style={{ marginLeft: 12 }}>
              <input type="checkbox" checked={form.is_staff}
                     onChange={(e) => setForm({ ...form, is_staff: e.target.checked })} /> staff (Django admin)
            </label>
            <label style={{ marginLeft: 12 }}>
              <input type="checkbox" checked={form.is_superuser}
                     onChange={(e) => setForm({ ...form, is_superuser: e.target.checked })} /> суперпользователь
            </label>
          </Field>
          <Field label="Роли" hint="коды ролей из справочника">
            <span>
              {(roles.data ?? []).map((r) => (
                <label key={r.code} style={{ marginRight: 12 }}>
                  <input type="checkbox"
                         checked={form.role_codes.includes(r.code)}
                         onChange={() => toggleRole(r.code)} /> {r.name || r.code}
                </label>
              ))}
            </span>
          </Field>
          <div className="toolbar">
            <button className="btn primary" disabled={busy || !form.username}
                    onClick={() => void submit()}>
              {form.id != null ? 'Сохранить' : 'Создать'}
            </button>
            <button className="btn" onClick={() => setForm(null)}>Отмена</button>
          </div>
        </div>
      )}
      <DataTable columns={cols} rows={users.data ?? []} empty="Нет пользователей" />
    </Card>
  );
}

export function AdminPage() {
  const auditList = useAsync<AuditEvent[]>(() => audit.events({}), []);
  const [preview, setPreview] = useState<{ title: string; rows: unknown[]; error?: unknown } | null>(null);

  const loadList = async (path: string, title: string) => {
    try {
      const rows = await apiList(path);
      setPreview({ title, rows });
    } catch (e) {
      setPreview({ title, rows: [], error: e });
    }
  };

  const auditCols: Column<AuditEvent>[] = [
    { key: 'created_at', title: 'Время', render: (r) => fmtDate(r.created_at) },
    { key: 'action', title: 'Действие', render: (r) => r.action ?? '—' },
    { key: 'entity_type', title: 'Сущность', render: (r) => r.entity_type ?? '—' },
    { key: 'actor', title: 'Актор', render: (r) => (r.actor != null ? String(r.actor) : '—') },
  ];

  return (
    <div>
      <PageHeader title="Администрирование" />

      <UsersCard />

      <Card title="Ссылки">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {ADMIN_LINKS.map((l) => (
            <li key={l.href} style={{ marginBottom: 6 }}>
              <a href={l.href} target="_blank" rel="noreferrer">{l.title}</a>
              <span className="muted"> — {l.desc}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Справочники и правила (просмотр)">
        <div className="toolbar">
          {ADMIN_ENDPOINTS.map((e) => (
            <button key={e.path} className="btn small" onClick={() => void loadList(e.path, e.title)}>
              {e.title}
            </button>
          ))}
        </div>
        {preview && (
          <div style={{ marginTop: 8 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{preview.title}</h3>
            {preview.error ? <ErrorBox error={preview.error} /> :
              preview.rows.length === 0 ? <Empty text="Пусто" /> :
              <pre className="json-block">{JSON.stringify(preview.rows.slice(0, 20), null, 2)}</pre>}
          </div>
        )}
      </Card>

      <Card title="Журнал аудита" actions={<button className="btn" onClick={auditList.reload}>Обновить</button>}>
        <ErrorBox error={auditList.error} onRetry={auditList.reload} />
        <DataTable columns={auditCols} rows={auditList.data ?? []} empty="Событий аудита нет или раздел недоступен" />
      </Card>
    </div>
  );
}
